import React, { useEffect, useState } from "react";
import "../../css/Analytics.css";
import axios from "axios";
import { useAuth } from "../../context/auth";
import AdminHeader from "../../components/header/AdminHeader";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PeopleIcon from "@mui/icons-material/People";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import InventoryIcon from "@mui/icons-material/Inventory";
import StarIcon from "@mui/icons-material/Star";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";

const Analytics = () => {
  const [auth] = useAuth();
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [topBuyers, setTopBuyers] = useState([]);
  const [activeCart, setActiveCart] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("all"); // all, 30days, 7days, today
  const apiUrl = `${process.env.REACT_APP_API}`;

  useEffect(() => {
    if (auth.token) {
      fetchAllData();
    }
  }, [auth.token]);

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        getOrders(),
        getUsers(),
        getProducts(),
        getActiveCart(),
      ]);
    } catch (err) {
      console.error("Error fetching analytics data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const getActiveCart = async () => {
    try {
      const response = await axios.get(`${apiUrl}/cart/active-cart`);
      setActiveCart(response.data.totalActiveCarts);
    } catch (err) {
      console.log(err);
    }
  };

  const getOrders = async () => {
    try {
      const { data } = await axios.get(`${apiUrl}/all-orders`);
      setOrders(data);
    } catch (err) {
      console.log(err);
    }
  };

  const getUsers = async () => {
    try {
      const { data } = await axios.get(`${apiUrl}/all-users`);
      setUsers(data);  // ✅ Now storing just the data
    } catch (err) {
      console.log(err);
    }
  };

  const getProducts = async () => {
    try {
      const { data } = await axios.get(`${apiUrl}/products`);
      setProducts(data);
    } catch (err) {
      console.log(err);
    }
  };

  // Filter orders by time range
  const getFilteredOrders = () => {
    const now = new Date();
    const filtered = orders.filter((order) => {
      const orderDate = new Date(order.createdAt);
      switch (timeRange) {
        case "today":
          return orderDate.toDateString() === now.toDateString();
        case "7days":
          const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return orderDate >= sevenDaysAgo;
        case "30days":
          const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          return orderDate >= thirtyDaysAgo;
        default:
          return true;
      }
    });
    return filtered;
  };

  const filteredOrders = getFilteredOrders();

  // Calculate total sales
  function calculateTotalSale(ordersList) {
    return ordersList.reduce((total, order) => {
      return total + parseFloat(order.total || 0);
    }, 0).toFixed(2);
  }

  // Calculate average order value
  function calculateAverageOrderValue(ordersList) {
    if (ordersList.length === 0) return 0;
    const total = ordersList.reduce((sum, order) => {
      return sum + parseFloat(order.total || 0);
    }, 0);
    return (total / ordersList.length).toFixed(2);
  }

  // Get top selling products
  function getTopSellingProducts(ordersList) {
    const productSales = {};

    ordersList.forEach((order) => {
      if (!order.products || !Array.isArray(order.products)) return;

      order.products.forEach((item) => {
        // Skip if product was deleted
        if (!item.product || !item.product._id) return;

        const productId = item.product._id;
        const productName = item.product.productName || "Unknown Product";
        const quantity = item.quantity || 0;
        const price = parseFloat(item.product.salePrice || item.product.productPrice || 0);
        const revenue = price * quantity;

        if (productSales[productId]) {
          productSales[productId].quantity += quantity;
          productSales[productId].revenue += revenue;
        } else {
          productSales[productId] = {
            name: productName,
            quantity: quantity,
            revenue: revenue,
            image: item.product.images && item.product.images.front,
          };
        }
      });
    });

    const sortedProducts = Object.values(productSales).sort((a, b) => b.quantity - a.quantity);
    return sortedProducts.slice(0, 5);
  }

  // Get low stock products
  function getLowStockProducts(productsList) {
    const lowStock = productsList
        .map((product) => {
          const totalStock = product.productSize.sizes.reduce(
              (sum, size) => sum + parseInt(size.quantity || 0),
              0
          );
          return {
            ...product,
            totalStock,
          };
        })
        .filter((product) => product.totalStock <= 10 && product.totalStock > 0)
        .sort((a, b) => a.totalStock - b.totalStock)
        .slice(0, 10);

    return lowStock;
  }

  // Get out of stock products
  function getOutOfStockProducts(productsList) {
    return productsList.filter((product) => {
      const totalStock = product.productSize.sizes.reduce(
          (sum, size) => sum + parseInt(size.quantity || 0),
          0
      );
      return totalStock === 0;
    });
  }

  // Get top buyers
  function getTopBuyers(ordersList) {
    const buyers = {};

    ordersList.forEach((order) => {
      // Skip orders without buyer information
      if (!order.buyer || !order.buyer._id) return;

      const buyerId = order.buyer._id;
      const orderTotal = parseFloat(order.total || 0);

      if (buyers[buyerId]) {
        buyers[buyerId].total += orderTotal;
        buyers[buyerId].orderCount += 1;
      } else {
        buyers[buyerId] = {
          id: buyerId,
          name: order.buyer.name || { first: "Unknown", last: "User" },
          email: order.buyer.email || "No email",
          phone: order.buyer.phone || "No phone",
          total: orderTotal,
          orderCount: 1,
        };
      }
    });

    const sortedBuyers = Object.values(buyers).sort((a, b) => b.total - a.total);
    return sortedBuyers.slice(0, 5);
  }

  // Calculate growth percentage
  function calculateGrowth(ordersList) {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);

    const last30Days = ordersList.filter((order) => {
      const orderDate = new Date(order.createdAt);
      return orderDate >= thirtyDaysAgo;
    });

    const previous30Days = ordersList.filter((order) => {
      const orderDate = new Date(order.createdAt);
      return orderDate >= sixtyDaysAgo && orderDate < thirtyDaysAgo;
    });

    const last30DaysRevenue = parseFloat(calculateTotalSale(last30Days));
    const previous30DaysRevenue = parseFloat(calculateTotalSale(previous30Days));

    if (previous30DaysRevenue === 0) return last30DaysRevenue > 0 ? 100 : 0;

    const growth = ((last30DaysRevenue - previous30DaysRevenue) / previous30DaysRevenue) * 100;
    return growth.toFixed(1);
  }

  const totalSaleTillDate = calculateTotalSale(filteredOrders);
  const averageOrderValue = calculateAverageOrderValue(filteredOrders);
  const topSellingProducts = getTopSellingProducts(filteredOrders);
  const lowStockProducts = getLowStockProducts(products);
  const outOfStockProducts = getOutOfStockProducts(products);
  const topBuyersData = getTopBuyers(filteredOrders);
  const growthPercentage = calculateGrowth(orders);
  const totalUsers = users.data || 0;
  const conversionRate = totalUsers > 0 ? ((filteredOrders.length / totalUsers) * 100).toFixed(1) : 0;

  if (isLoading) {
    return (
        <>
          <AdminHeader />
          <div className="container pt-5 mt-5 text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading analytics...</p>
          </div>
        </>
    );
  }

  return (
      <>
        <AdminHeader />
        <div className="analytics-wrapper">
          <div className="analytics-container">
            {/* Header */}
            <div className="analytics-header">
              <div>
                <h1 className="analytics-title">Analytics Dashboard</h1>
                <p className="analytics-subtitle">Track your store's performance and insights</p>
              </div>

              {/* Time Range Filter */}
              <div className="time-filter">
                <select
                    value={timeRange}
                    onChange={(e) => setTimeRange(e.target.value)}
                    className="time-select"
                >
                  <option value="all">All Time</option>
                  <option value="30days">Last 30 Days</option>
                  <option value="7days">Last 7 Days</option>
                  <option value="today">Today</option>
                </select>
              </div>
            </div>

            {/* Key Metrics Cards */}
            <div className="metrics-grid">
              {/* Total Revenue */}
              <div className="metric-card revenue-card">
                <div className="metric-icon">
                  <AttachMoneyIcon />
                </div>
                <div className="metric-content">
                  <p className="metric-label">Total Revenue</p>
                  <h2 className="metric-value">${totalSaleTillDate}</h2>
                  <div className={`metric-trend ${growthPercentage >= 0 ? 'positive' : 'negative'}`}>
                    {growthPercentage >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                    <span>{Math.abs(growthPercentage)}% vs last month</span>
                  </div>
                </div>
              </div>

              {/* Total Orders */}
              <div className="metric-card orders-card">
                <div className="metric-icon">
                  <ShoppingCartIcon />
                </div>
                <div className="metric-content">
                  <p className="metric-label">Total Orders</p>
                  <h2 className="metric-value">{filteredOrders.length}</h2>
                  <p className="metric-detail">
                    Avg: ${averageOrderValue} per order
                  </p>
                </div>
              </div>

              {/* Total Customers */}
              <div className="metric-card customers-card">
                <div className="metric-icon">
                  <PeopleIcon />
                </div>
                <div className="metric-content">
                  <p className="metric-label">Total Customers</p>
                  <h2 className="metric-value">{totalUsers}</h2>
                  <p className="metric-detail">
                    {conversionRate}% conversion rate
                  </p>
                </div>
              </div>

              {/* Active Carts */}
              <div className="metric-card cart-card">
                <div className="metric-icon">
                  <LocalShippingIcon />
                </div>
                <div className="metric-content">
                  <p className="metric-label">Active Carts</p>
                  <h2 className="metric-value">{activeCart}</h2>
                  <p className="metric-detail">
                    Potential customers
                  </p>
                </div>
              </div>
            </div>

            {/* Two Column Layout */}
            <div className="analytics-grid">
              {/* Left Column */}
              <div className="analytics-column">
                {/* Top Selling Products */}
                <div className="analytics-card">
                  <div className="card-header">
                    <h3>
                      <StarIcon /> Top Selling Products
                    </h3>
                    <span className="card-badge">{timeRange === 'all' ? 'All Time' : timeRange}</span>
                  </div>
                  <div className="product-list">
                    {topSellingProducts.length > 0 ? (
                        topSellingProducts.map((product, index) => (
                            <div key={index} className="product-item">
                              <div className="product-rank">#{index + 1}</div>
                              <img
                                  src={product.image}
                                  alt={product.name}
                                  className="product-thumb"
                              />
                              <div className="product-details">
                                <p className="product-name">{product.name}</p>
                                <p className="product-stats">
                                  {product.quantity} sold • ${product.revenue.toFixed(2)} revenue
                                </p>
                              </div>
                            </div>
                        ))
                    ) : (
                        <p className="empty-state">No sales data available</p>
                    )}
                  </div>
                </div>

                {/* Top Customers */}
                <div className="analytics-card">
                  <div className="card-header">
                    <h3>
                      <PeopleIcon /> Top Customers
                    </h3>
                    <span className="card-badge">Best Buyers</span>
                  </div>
                  <div className="customer-list">
                    {topBuyersData.length > 0 ? (
                        topBuyersData.map((buyer, index) => (
                            <div key={buyer.id} className="customer-item">
                              <div className="customer-rank">#{index + 1}</div>
                              <div className="customer-avatar">
                                {buyer.name && buyer.name.first && buyer.name.last
                                    ? `${buyer.name.first.charAt(0)}${buyer.name.last.charAt(0)}`
                                    : "??"}
                              </div>
                              <div className="customer-details">
                                <p className="customer-name">
                                  {buyer.name && buyer.name.first && buyer.name.last
                                      ? `${buyer.name.first} ${buyer.name.last}`
                                      : "Unknown User"}
                                </p>
                                <p className="customer-email">{buyer.email || "No email"}</p>
                                <p className="customer-stats">
                                  {buyer.orderCount} orders • ${buyer.total.toFixed(2)} spent
                                </p>
                              </div>
                            </div>
                        ))
                    ) : (
                        <p className="empty-state">No customer data available</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="analytics-column">
                {/* Financial Summary */}
                <div className="analytics-card">
                  <div className="card-header">
                    <h3>
                      <AttachMoneyIcon /> Financial Summary
                    </h3>
                  </div>
                  <div className="financial-stats">
                    <div className="stat-row">
                      <span className="stat-label">Total Revenue</span>
                      <span className="stat-value">${totalSaleTillDate}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">Estimated Taxes (5%)</span>
                      <span className="stat-value">
                      ${(totalSaleTillDate * 0.05).toFixed(2)}
                    </span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">Net Revenue (after tax)</span>
                      <span className="stat-value highlight">
                      ${(totalSaleTillDate * 0.95).toFixed(2)}
                    </span>
                    </div>
                    <div className="stat-divider"></div>
                    <div className="stat-row">
                      <span className="stat-label">Average Order Value</span>
                      <span className="stat-value">${averageOrderValue}</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">Total Orders</span>
                      <span className="stat-value">{filteredOrders.length}</span>
                    </div>
                  </div>
                </div>

                {/* Inventory Alerts */}
                <div className="analytics-card alert-card">
                  <div className="card-header">
                    <h3>
                      <InventoryIcon /> Inventory Alerts
                    </h3>
                  </div>

                  {/* Out of Stock */}
                  {outOfStockProducts.length > 0 && (
                      <div className="alert-section">
                        <div className="alert-header danger">
                          <strong>Out of Stock</strong>
                          <span className="alert-count">{outOfStockProducts.length}</span>
                        </div>
                        <div className="alert-list">
                          {outOfStockProducts.slice(0, 5).map((product) => (
                              <div key={product._id} className="alert-item">
                                <span className="alert-dot danger"></span>
                                {product.productName}
                              </div>
                          ))}
                          {outOfStockProducts.length > 5 && (
                              <p className="alert-more">
                                +{outOfStockProducts.length - 5} more items
                              </p>
                          )}
                        </div>
                      </div>
                  )}

                  {/* Low Stock */}
                  {lowStockProducts.length > 0 && (
                      <div className="alert-section">
                        <div className="alert-header warning">
                          <strong>Low Stock (≤10 units)</strong>
                          <span className="alert-count">{lowStockProducts.length}</span>
                        </div>
                        <div className="alert-list">
                          {lowStockProducts.slice(0, 5).map((product) => (
                              <div key={product._id} className="alert-item">
                                <span className="alert-dot warning"></span>
                                {product.productName}
                                <span className="stock-count">{product.totalStock} left</span>
                              </div>
                          ))}
                          {lowStockProducts.length > 5 && (
                              <p className="alert-more">
                                +{lowStockProducts.length - 5} more items
                              </p>
                          )}
                        </div>
                      </div>
                  )}

                  {outOfStockProducts.length === 0 && lowStockProducts.length === 0 && (
                      <div className="alert-success">
                        <p>✓ All products are well stocked!</p>
                      </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Stats Bar */}
            <div className="quick-stats">
              <div className="quick-stat-item">
                <p className="quick-stat-label">Total Products</p>
                <p className="quick-stat-value">{products.length}</p>
              </div>
              <div className="quick-stat-item">
                <p className="quick-stat-label">Conversion Rate</p>
                <p className="quick-stat-value">{conversionRate}%</p>
              </div>
              <div className="quick-stat-item">
                <p className="quick-stat-label">Avg Items/Order</p>
                <p className="quick-stat-value">
                  {filteredOrders.length > 0
                      ? (
                          filteredOrders.reduce(
                              (sum, order) => sum + (order.products ? order.products.length : 0),
                              0
                          ) / filteredOrders.length
                      ).toFixed(1)
                      : 0}
                </p>
              </div>
              <div className="quick-stat-item">
                <p className="quick-stat-label">Active Customers</p>
                <p className="quick-stat-value">{activeCart}</p>
              </div>
            </div>
          </div>
        </div>
      </>
  );
};

export default Analytics;